import { Container, Stack, Typography } from '@mui/material';
import { useSeekaConverge } from '@seeka-labs/converge-react';
import { useCart } from '@shopify/hydrogen-react';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { FullScreenLoading } from '../../components/fullScreenLoading';
import { exchangeCodeForToken } from '../../shared/shopify/auth/customer/callback';

const ShopifyAuthCallback = () => {
  const [error, setError] = useState<string | null>(null);

  const { buyerIdentityUpdate } = useCart();

  const seekaConverge = useSeekaConverge();

  useEffect(() => {
    const handleCallback = async () => {
      if (typeof window === 'undefined') {
        return
      }
      // Get code from URL parameters
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      // Validate state to prevent CSRF
      const storedState = localStorage.getItem('auth-state');
      if (state !== storedState) {
        setError('Invalid state parameter. Authentication failed.');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      try {
        const result = await exchangeCodeForToken(code);

        // Check if this was a wholesale login request
        const isWholesaleRequest = localStorage.getItem('auth-wholesale') === 'true';
        localStorage.removeItem('auth-wholesale'); // Clean up

        // Calculate expiration in days (js-cookie expects days)
        const expiresInDays = result.expires_in / (24 * 60 * 60); // Convert seconds to days

        // Store the tokens in cookies that expire with the token
        Cookies.set('shopify_access_token', result.access_token, {
          expires: expiresInDays,
          secure: true,
          sameSite: 'strict'
        });
        Cookies.set('shopify_id_token', result.id_token, {
          expires: expiresInDays,
          secure: true,
          sameSite: 'strict'
        });

        // Cookies.set('shopify_refresh_token', result.refresh_token, {
        //   expires: expiresInDays,
        //   secure: true,
        //   sameSite: 'strict'
        // });

        const response = await fetch(
          `https://shopify.com/${process.env.GATSBY_SHOPIFY_SHOP_ID}/account/customer/api/2025-01/graphql`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': result.access_token,
            },
            body: JSON.stringify({
              operationName: 'GetCustomerDetails',
              query: `query { 
                  customer { 
                    emailAddress { emailAddress }
                    phoneNumber { phoneNumber }
                    tags
                    firstName
                    lastName
                    id
                    lastIncompleteCheckout {
                      id
                    }
                  } 
                }`,
              variables: {},
            }),
          },
        );
        if (response.ok) {
          const responseJson: {
            data: {
              customer: {
                emailAddress?: {
                  emailAddress?: string
                },
                firstName?: string,
                lastName?: string,
                id,
                phoneNumber?: {
                  phoneNumber?: string
                },
                lastIncompleteCheckout?: {
                  id?: string
                },
                tags?: string[]
              }
            }
          } = await response.json();
          // console.log('heree responseJson', responseJson)

          buyerIdentityUpdate({
            customerAccessToken: result.access_token,
            email: responseJson.data.customer?.emailAddress?.emailAddress,
            phone: responseJson.data.customer?.phoneNumber?.phoneNumber,
          })

          if (responseJson.data.customer.emailAddress?.emailAddress) {
            Cookies.set('shopify_customer_email', responseJson.data.customer.emailAddress.emailAddress, {
              expires: expiresInDays,
              secure: true,
              sameSite: 'strict'
            });
          }
          else {
            Cookies.remove('shopify_customer_email');
          }

          try {
            await seekaConverge.identity.mergeProfile({
              email: responseJson.data.customer?.emailAddress?.emailAddress ? [responseJson.data.customer?.emailAddress?.emailAddress] : [],
              phone: responseJson.data.customer?.phoneNumber?.phoneNumber ? [responseJson.data.customer.phoneNumber.phoneNumber] : [],
              firstName: responseJson.data.customer?.firstName ? [responseJson.data.customer?.firstName] : [],
              lastName: responseJson.data.customer?.lastName ? [responseJson.data.customer?.lastName] : [],
              shopify: {
                customerId: responseJson.data.customer?.id,
                checkoutId: responseJson.data.customer?.lastIncompleteCheckout?.id ? [responseJson.data.customer?.lastIncompleteCheckout?.id] : []
              }
            })
            await seekaConverge.track.userLogin();
          }
          catch (err) {
            console.error('Failed to merge profile with Seeka Converge', err);
          }

          let tags = responseJson.data.customer?.tags || [];

          // Add wholesale request tag if needed
          // if (isWholesaleRequest && !tags.includes('Requested wholesale')) {
          //   tags = [...tags, 'Requested wholesale'];

          //   // Update customer tags in Shopify
          //   await fetch(
          //     `https://shopify.com/${process.env.GATSBY_SHOPIFY_SHOP_ID}/account/customer/api/2025-01/graphql/api/2025-01/graphql.json`,
          //     {
          //       method: 'POST',
          //       headers: {
          //         'Content-Type': 'application/json',
          //         'Authorization': result.access_token,
          //       },
          //       body: JSON.stringify({
          //         operationName: 'UpdateCustomerTags',
          //         query: `mutation customerUpdate($input: CustomerUpdateInput!) {
          //           customerUpdate(input: $input) {
          //             customer {
          //               tags
          //             }
          //             userErrors {
          //               field
          //               message
          //             }
          //           }
          //         }`,
          //         variables: {
          //           input: {
          //             tags: tags
          //           }
          //         },
          //       }),
          //     }
          //   );
          // }

          if (tags.length > 0) {
            Cookies.set('shopify_customer_tags', JSON.stringify(tags), {
              expires: expiresInDays,
              secure: true,
              sameSite: 'strict'
            });

            const isWholesale = tags.includes('Approved wholesaler');
            if (isWholesale) {
              Cookies.set('shopify_customer_wholesale', '1', {
                expires: expiresInDays,
                secure: true,
                sameSite: 'strict'
              });
            }
            else {
              Cookies.remove('shopify_customer_wholesale');
            }
          }
          else {
            Cookies.remove('shopify_customer_tags');
            Cookies.remove('shopify_customer_email');
            Cookies.remove('shopify_customer_wholesale');
          }
        }

        // Redirect to the account page or homepage
        window.location.href = window.location.protocol + '//' + window.location.hostname + '/products'; // must refresh window so auth cookies take effect in UI
      } catch (err) {
        console.error('Failed to complete authentication', err);
        setError('We couldn\'t log you in.');
      }
    };

    handleCallback();
  }, []);

  if (error) {
    return (
      <Container sx={{ marginTop: '96px' }}>
        <Stack spacing={2} direction="column" justifyContent="center" alignItems="center">
          <Typography variant="h2" color="error">
            Oh no!
          </Typography>
          <Typography variant="h4" color="error">
            {error}
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <FullScreenLoading text='Logging you into Bloo cyber systems' />
  );
};

export default ShopifyAuthCallback;